
import { asCurrency, asTimelessDate, Column, Columns, div, Flex } from "lqx-react";
import React, { useMemo } from "react";
import { BoundPolicy } from "../api/boundPolicy";
import { PolicyApprovedLimits } from "../api/policyApprovedLimit";
import { CoveredAssetTypes, PolicyTypes } from "../api/reference-data";

const useReferenceData = () => useMemo(() => {
    return {
        policyTypes: Object.assign({}, ...PolicyTypes.map((x) => ({ [ x.id ]: x.name }))),
        coveredAssetTypes: Object.assign({}, ...CoveredAssetTypes.map((x) => ({ [ x.id ]: x.name }))),
    }
}, [ true ]);

export const BoundPolicySummary = ({ policy, policyApprovedLimits }: { policy: BoundPolicy, policyApprovedLimits: PolicyApprovedLimits }) => {
    const referenceData = useReferenceData();

    const thisPolicyApprovedLimits = useMemo(() => policyApprovedLimits.approvedLimits.filter(al => al.policyId == policy.id), [ policy.id ])
    const total = useMemo(() => ({
        approvedLimit: thisPolicyApprovedLimits.reduce((total, current) => total += current.maxCreditLimit, 0),
        invoicesCount: policyApprovedLimits.approvedLimits.models.reduce((alTotal, currentAl) => alTotal += currentAl.debtors.models.filter(debtor => debtor.isSelected).reduce((total, currentDebtor) => total += currentDebtor.invoices.models.length, 0), 0),
        invoicesAmount:policyApprovedLimits.approvedLimits.models.reduce((alTotal, currentAl) => alTotal += currentAl.debtors.models.filter(debtor => debtor.isSelected).reduce((total, currentDebtor) => total += currentDebtor.invoices.models.reduce((invTotal, currentInv) => invTotal += currentInv.assetDetails.faceValue, 0), 0), 0)
    }), [policyApprovedLimits.approvedLimits[ '_changeToken' ] ]);

    return <>
        <SummaryContainer>
            <SummaryHeader>
                <Columns>
                    <Column>
                        Digitized on { asTimelessDate(policy.createdOn) }
                    </Column>
                    <Column><Flex grow></Flex></Column>
                    <Column>
                        <Columns>
                            <Column style={ { "width": "500px" } }>Inception Date: { asTimelessDate(policy.inceptionDate) }</Column>
                            <Column style={ { "width": "500px" } }>Expiration Date: { asTimelessDate(policy.expiryDate) }</Column>
                        </Columns>
                    </Column>
                </Columns>
            </SummaryHeader>
            <Columns>
                <Column><SummaryPolicyName>{ policy.name }</SummaryPolicyName></Column>
                <Column>
                    <Columns>
                        <Column></Column>
                        <Column><SummaryPolicyStatus>{ policy.status }</SummaryPolicyStatus></Column>
                    </Columns>
                </Column>
            </Columns>
            <Columns>
                <Column>
                    <SummaryPolicyDetails>{ referenceData.policyTypes[ policy.policyTypeId ] || 'N/A' }  |  { referenceData.coveredAssetTypes[ policy.coveredAssetTypeId ] || 'N/A' } | Policy no. { policy.carrierPolicyNumber || 'N/A' }</SummaryPolicyDetails>
                </Column>
            </Columns>
            <Columns style={ { "marginTop": "10px" } }>
                <Column>
                    <SummaryLabel>Policy Limit Of Liability:</SummaryLabel>
                    <SummaryFormValue>{ asCurrency(policy.liabilityLimit) }</SummaryFormValue>
                </Column>
                <Column>
                    <SummaryLabel>Premium Rate:</SummaryLabel>
                    <SummaryFormValue>{ policy.premiumRate.toFixed(4) }%</SummaryFormValue>
                </Column>
                <Column>
                    <SummaryLabel style={ { "width": "300px" } }>Insurance Carrier:</SummaryLabel>
                    <SummaryFormValue style={ { "width": "300px" } }>{ policy.policyInsuranceCarriers.models.length > 0 ? policy.policyInsuranceCarriers.models[ 0 ].insuranceCarrierName : 'N/A' }</SummaryFormValue>
                </Column>
                <Column>
                    <SummaryLabel>Broker:</SummaryLabel>
                    <SummaryFormValue>{ policy.broker ? policy.broker.name : 'N/A' }</SummaryFormValue>
                </Column>
                <Column>
                    <SummaryTotalValue>{ thisPolicyApprovedLimits.length } Approved Limit(s) Totaling { asCurrency(total.approvedLimit / 1000000) }MM</SummaryTotalValue>
                    <SummaryTotalValue>{ total.invoicesCount } Linked Invoices Totaling { asCurrency(total.invoicesAmount / 1000000) }MM</SummaryTotalValue>
                </Column>
            </Columns>
        </SummaryContainer>
    </>
}

const SummaryLabel = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '11px',
    lineHeight: '15px',
    color: '#8695A4',
    paddingTop: '5px',
    marginTop: '3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: "130px"

});

const SummaryFormValue = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '11px',
    lineHeight: '15px',
    paddingTop: '5px',
    marginTop: '3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: "120px",
    color: '#4197d6',
});

const SummaryContainer = div({
    background: '#FFFFFF',
    border: '1px solid #4197d6',
    borderRadius: '4px',
    paddingBottom: '20px',
    marginBottom: '50px',
    marginTop: '20px'
});

const SummaryHeader = div({
    paddingTop: '20px',
    backgroundColor: '#4197d6',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    color: 'white',
    fontWeight: 500,
    textAlign: 'left',
    height: '60px',
    marginBottom: '20px',
});

const SummaryPolicyName = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontSize: '15px',
    color: '#4197d6',
    fontWeight: 500
});

const SummaryTotalValue = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontSize: '11px',
    color: '#4197d6',
    fontWeight: 500,
    width: '260px'
});

const SummaryPolicyDetails = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontSize: '13px',
    fontWeight: 500
});

const SummaryPolicyStatus = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontSize: '16px',
    color: '#01c38d',
    lineHeight: '20px',
    fontWeight: 600,
    marginLeft: '90px',
    marginTop: '15px'
});
